/**
 * custom.main.js
 * Description: Main JS for Zigma Store custom theme — header scroll, accordions,
 *   scroll reveals, carousel arrows, tabs, video play, slideshow, combo builder, toast
 * Author: Leo Liquid / Horizon Builder Squad
 * Version: 1.0.0
 * No dependencies — vanilla JS, defer loaded.
 */

(function () {
  'use strict';

  /* =========================================================================
     1. SCROLL REVEALS (IntersectionObserver)
     ========================================================================= */
  /* Shared card observer — reused for dynamically injected cards */
  var cardObserver = null;

  function observeCard(card) {
    if (!cardObserver) return;
    /* Skip cards already revealed */
    if (card.classList.contains('pcard-revealed')) return;
    cardObserver.observe(card);
  }

  function initReveals() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var els = document.querySelectorAll('[data-reveal], [data-reveal-stagger]');
    if (els.length) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -50px 0px' });

      els.forEach(function (el) { observer.observe(el); });
    }

    /* Product card reveal — fade in + slide up with stagger delay */
    cardObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var card = entry.target;
          var rect = card.getBoundingClientRect();
          var viewportWidth = window.innerWidth;
          var col = Math.floor(rect.left / (viewportWidth / 4));
          card.style.transitionDelay = (col * 100) + 'ms';
          card.classList.add('pcard-revealed');
          cardObserver.unobserve(card);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -30px 0px' });

    document.querySelectorAll('[data-pcard-reveal]').forEach(observeCard);

    /* Watch for cards injected via AJAX (collection filters / pagination / infinite scroll).
       Use #ResultsList (Horizon collection wrapper) or fall back to body.
       We also watch class attribute changes because the theme's morph step reuses
       existing card nodes and strips the `pcard-revealed` class on filter/sort
       (the class is JS-added, never in the server HTML), which leaves those cards
       stuck at opacity:0 until we re-observe them. */
    var gridRoot = document.querySelector('#ResultsList, .product-grid, results-list') || document.body;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.type === 'attributes') {
          var target = mutation.target;
          if (target.nodeType === 1 && target.matches && target.matches('[data-pcard-reveal]:not(.pcard-revealed)')) {
            observeCard(target);
          }
          return;
        }
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (node.hasAttribute && node.hasAttribute('data-pcard-reveal')) {
            observeCard(node);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('[data-pcard-reveal]').forEach(observeCard);
          }
        });
      });
    }).observe(gridRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  }

  /* =========================================================================
     2. HEADER SCROLL (transparent -> solid via IntersectionObserver)
     ========================================================================= */
  function initHeaderScroll() {
    var header = document.querySelector('[data-header-transparent], .custom-header--transparent');
    var hero = document.querySelector('.custom-hero-banner');
    if (!hero || !header) return;

    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        header.classList.toggle('custom-header--solid', !e.isIntersecting);
      });
    }, { threshold: 0 }).observe(hero);
  }

  /* =========================================================================
     3. ACCORDION (FAQ)
     ========================================================================= */
  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(function (accordion) {
      accordion.querySelectorAll('[data-accordion-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var expanded = this.getAttribute('aria-expanded') === 'true';
          // Close all siblings if single-mode (default)
          var parent = this.closest('[data-accordion]');
          parent.querySelectorAll('[data-accordion-trigger]').forEach(function (t) {
            t.setAttribute('aria-expanded', 'false');
          });
          // Toggle current
          if (!expanded) {
            this.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* =========================================================================
     4. CAROUSEL ARROWS (scroll-snap based)
     ========================================================================= */
  function initCarousels() {
    /* Event delegation — works for all carousels including those inside hidden tabs */
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-carousel-prev], [data-carousel-next]');
      if (!btn) return;

      var wrapper = btn.closest('.custom-scroll-wrapper') ||
                    btn.closest('.custom-collection-carousel__wrapper') ||
                    btn.closest('.custom-product-recommendations__grid');
      if (!wrapper) return;

      var track = wrapper.querySelector('[data-carousel-track]') ||
                  wrapper.querySelector('.custom-product-scroll');
      if (!track) return;

      var firstChild = track.firstElementChild;
      var scrollAmt = firstChild ? firstChild.offsetWidth + 16 : 300;
      var direction = btn.hasAttribute('data-carousel-prev') ? -1 : 1;

      track.scrollBy({ left: direction * scrollAmt, behavior: 'smooth' });
    });
  }

  /* =========================================================================
     5. TABS (Collection tabs)
     ========================================================================= */
  function initTabs() {
    document.querySelectorAll('.custom-collection-tabs').forEach(function (section) {
      var tabs = section.querySelectorAll('[data-tab-index]');
      var panels = section.querySelectorAll('.custom-collection-tabs__panel');

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var idx = parseInt(this.dataset.tabIndex, 10);

          tabs.forEach(function (t) {
            t.classList.remove('custom-collection-tabs__tab--active');
            t.setAttribute('aria-selected', 'false');
          });
          this.classList.add('custom-collection-tabs__tab--active');
          this.setAttribute('aria-selected', 'true');

          panels.forEach(function (p, i) {
            p.classList.toggle('custom-collection-tabs__panel--active', i === idx);
          });
        });
      });
    });
  }

  /* =========================================================================
     6. VIDEO PLAY (lazy load iframe on click)
     ========================================================================= */
  function initVideos() {
    document.querySelectorAll('[data-video-play]').forEach(function (player) {
      player.addEventListener('click', function () {
        var container = this.closest('[data-video-url]') || this;
        var url = container.dataset.videoUrl;
        if (!url) return;

        var iframeWrap = container.querySelector('[data-video-iframe]');
        if (!iframeWrap || iframeWrap.classList.contains('active')) return;

        var embedUrl = '';
        if (url.indexOf('youtube.com') !== -1 || url.indexOf('youtu.be') !== -1) {
          var videoId = url.match(/(?:v=|\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]+)/);
          if (videoId) embedUrl = 'https://www.youtube.com/embed/' + videoId[1] + '?autoplay=1&rel=0';
        } else if (url.indexOf('vimeo.com') !== -1) {
          var vimeoId = url.match(/vimeo\.com\/(\d+)/);
          if (vimeoId) embedUrl = 'https://player.vimeo.com/video/' + vimeoId[1] + '?autoplay=1';
        }

        if (!embedUrl) return;

        iframeWrap.innerHTML = '<iframe src="' + embedUrl + '" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>';
        iframeWrap.classList.add('active');

        // Hide play button and cover
        var playBtn = container.querySelector('.custom-video-section__play');
        if (playBtn) playBtn.style.display = 'none';
        var cover = container.querySelector('img');
        if (cover) cover.style.display = 'none';
      });
    });
  }

  /* =========================================================================
     7. HERO SLIDESHOW
     ========================================================================= */
  function initSlideshow() {
    document.querySelectorAll('.custom-hero-banner').forEach(function (hero) {
      var slides = hero.querySelectorAll('.custom-hero-banner__slide');
      var dots = hero.querySelectorAll('.custom-hero-banner__dot');
      if (slides.length < 2) return;

      var current = 0;
      var autoplay = hero.dataset.autoplay === 'true';
      var speed = (parseInt(hero.dataset.speed, 10) || 5) * 1000;
      var interval = null;

      function goTo(idx) {
        slides.forEach(function (s, i) {
          s.classList.toggle('custom-hero-banner__slide--active', i === idx);
        });
        dots.forEach(function (d, i) {
          d.classList.toggle('custom-hero-banner__dot--active', i === idx);
        });
        current = idx;
      }

      function next() {
        goTo((current + 1) % slides.length);
      }

      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          goTo(parseInt(this.dataset.slideTarget, 10));
          resetAutoplay();
        });
      });

      function startAutoplay() {
        if (autoplay) interval = setInterval(next, speed);
      }

      function resetAutoplay() {
        clearInterval(interval);
        startAutoplay();
      }

      hero.addEventListener('mouseenter', function () { clearInterval(interval); });
      hero.addEventListener('mouseleave', startAutoplay);

      startAutoplay();
    });
  }

  /* =========================================================================
     8. COMBO BUILDER (Monte seu Kit)
     ========================================================================= */
  function initComboBuilder() {
    document.querySelectorAll('.custom-combo-builder').forEach(function (section) {
      var steps = section.querySelectorAll('[data-combo-step]');
      var totalSteps = steps.length;
      if (totalSteps === 0) return;

      var currentStep = 0;
      var prevBtn = section.querySelector('[data-combo-prev]');
      var nextBtn = section.querySelector('[data-combo-next]');
      var finishBtn = section.querySelector('[data-combo-finish]');
      var progressFill = section.querySelector('[data-combo-fill]');
      var progressText = section.querySelector('[data-combo-progress-text]');

      function updateStep() {
        steps.forEach(function (s, i) {
          s.classList.toggle('custom-combo-builder__step--active', i === currentStep);
        });
        if (prevBtn) prevBtn.disabled = currentStep === 0;
        if (nextBtn) nextBtn.style.display = currentStep === totalSteps - 1 ? 'none' : '';
        if (finishBtn) finishBtn.style.display = currentStep === totalSteps - 1 ? '' : 'none';
        if (progressFill) progressFill.style.width = ((currentStep + 1) / totalSteps * 100) + '%';
        if (progressText) progressText.textContent = 'Item ' + (currentStep + 1) + '/' + totalSteps;
      }

      if (nextBtn) nextBtn.addEventListener('click', function () {
        if (currentStep < totalSteps - 1) { currentStep++; updateStep(); }
      });
      if (prevBtn) prevBtn.addEventListener('click', function () {
        if (currentStep > 0) { currentStep--; updateStep(); }
      });

      // Swatch and size selection
      section.querySelectorAll('[data-combo-swatch]').forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          var group = this.closest('.custom-combo-builder__swatches');
          group.querySelectorAll('.custom-swatch').forEach(function (s) {
            s.classList.remove('custom-swatch--selected');
          });
          this.classList.add('custom-swatch--selected');
        });
      });

      section.querySelectorAll('[data-combo-size-btn]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var group = this.closest('.custom-combo-builder__sizes');
          group.querySelectorAll('.custom-size-btn').forEach(function (b) {
            b.classList.remove('custom-size-btn--selected');
          });
          this.classList.add('custom-size-btn--selected');
        });
      });

      // Finish button — add all variants to cart
      if (finishBtn) {
        finishBtn.addEventListener('click', function () {
          var items = [];
          section.querySelectorAll('[data-combo-variant-id]').forEach(function (input) {
            items.push({ id: parseInt(input.value, 10), quantity: 1 });
          });

          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          })
          .then(function (r) { return r.json(); })
          .then(function () {
            showToast('Combo adicionado a sacola!');
          })
          .catch(function () {
            showToast('Erro ao adicionar combo. Tente novamente.', true);
          });
        });
      }

      updateStep();
    });
  }

  /* =========================================================================
     9. TOAST NOTIFICATIONS
     ========================================================================= */
  function showToast(message, isError) {
    var existing = document.querySelector('.custom-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'custom-toast' + (isError ? ' custom-toast--error' : '');
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.classList.add('custom-toast--visible');
    });

    setTimeout(function () {
      toast.classList.remove('custom-toast--visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 3000);
  }

  // Expose toast globally for other scripts
  window.customShowToast = showToast;

  /* =========================================================================
     INIT ALL MODULES
     ========================================================================= */
  document.addEventListener('DOMContentLoaded', function () {
    initReveals();
    initHeaderScroll();
    initAccordions();
    initCarousels();
    initTabs();
    initVideos();
    initSlideshow();
    initComboBuilder();
  });

})();